import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Skeleton } from "@mui/material";
import SavedPanelsScreen from "./SavedPanelsScreen";
import NewPanelModal from "./NewPanelModal";
import { useAppStore } from "../../../../providers/AppStore";
import {
  RootStateServiceLogicBuilder,
  serviceLogicBuilderActions,
} from "../../store/service-logic-builder-slice";
import useTheme from "../../../../common/hooks/useTheme";

const PanelListSkeleton = () => {
  const theme = useTheme();
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      {/* Search bar + button skeleton */}
      <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
        <Skeleton
          variant="rectangular"
          sx={{
            flex: 1,
            height: "38px",
            bgcolor: theme.colorTokens.N[30],
            borderRadius: "8px",
          }}
          animation="pulse"
        />
        <Skeleton
          variant="rectangular"
          sx={{
            width: "140px",
            height: "38px",
            bgcolor: theme.colorTokens.N[30],
            borderRadius: "8px",
          }}
          animation="pulse"
        />
      </div>
      {/* Table header skeleton */}
      <Skeleton
        variant="rectangular"
        sx={{
          width: "60%",
          height: "19px",
          bgcolor: theme.colorTokens.N[30],
          borderRadius: "4px",
        }}
        animation="pulse"
      />
      {/* Row skeletons */}
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        {Array.from({ length: 5 }).map((_, index) => (
          <Skeleton
            key={index}
            variant="rectangular"
            sx={{
              width: "100%",
              height: "45px",
              bgcolor: theme.colorTokens.N[30],
              borderRadius: "8px",
            }}
            animation="pulse"
          />
        ))}
      </div>
    </div>
  );
};

const LabBuilder: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { ASGetLabPanelsList } = useAppStore();
  const savedLabPanels = useSelector(
    (state: RootStateServiceLogicBuilder) =>
      state.serviceLogicBuilder.savedLabPanels
  );
  // Whether the panels have been fetched at least once this session (persists in
  // Redux across remounts). Distinguishes "not loaded" from "loaded but empty".
  const labPanelsLoaded = useSelector(
    (state: RootStateServiceLogicBuilder) =>
      state.serviceLogicBuilder.labPanelsLoaded
  );
  // The shared services loading flag, raised while a panel is being deleted
  // (PanelDetailsViewWrapper navigates back here first, then deletes and
  // refetches) and by the Services layout's own load. Without it the listing
  // sat there showing the deleted panel until the refetch landed.
  const isRefetching = useSelector(
    (state: RootStateServiceLogicBuilder) => state.serviceLogicBuilder.loading
  );
  const [openModal, setOpenModal] = useState(false);
  // Only show the skeleton on the very first load. On client-side nav back to
  // this screen the panels are already cached, so we neither refetch nor flash
  // the loader (create/delete keep the cache fresh via ASGetLabPanelsList).
  const [isLoading, setIsLoading] = useState(!labPanelsLoaded);

  useEffect(() => {
    if (labPanelsLoaded) return;
    let cancelled = false;
    ASGetLabPanelsList().finally(() => {
      if (!cancelled) setIsLoading(false);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePanelSaved = async () => {
    // A new panel always lands in the Custom tab — switch there so the user
    // sees it, even if they opened the modal from the "Quest default" tab.
    dispatch(serviceLogicBuilderActions.setActiveLabTab("custom"));
    await ASGetLabPanelsList();
  };

  const handlePanelClick = (panel: any) => {
    // The detail screen resolves the panel by id (user or provider), so the
    // route only needs the id.
    navigate(`/client/services/lab/${panel.id}`);
  };

  return (
    <div style={styles.root}>
      {isLoading || isRefetching ? (
        <PanelListSkeleton />
      ) : (
        // Always render the panels screen (even with no custom panels) so the
        // built-in "Quest default" tab is reachable — the old empty state hid it.
        <SavedPanelsScreen
          panels={savedLabPanels}
          setOpenModal={setOpenModal}
          onPanelClick={handlePanelClick}
        />
      )}
      <NewPanelModal
        openModal={openModal}
        handleClose={() => setOpenModal(false)}
        onPanelSaved={handlePanelSaved}
      />
    </div>
  );
};

export default LabBuilder;

const styles = {
  root: {
    height: "100%",
    display: "flex",
    flexDirection: "column" as "column",
  },
};
